// src/components/Plan/PlanQuestionnaire.jsx - FIXED STRUCTURE
import React, { useState, useEffect } from 'react';
import { planService } from '../../services/planService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './PlanQuestionnaire.css';

const PlanQuestionnaire = ({ onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const { getCurrencySymbol, currency } = useCurrency();

  const [formData, setFormData] = useState({
    planning_goal: '',
    investment_horizon: '',
    income_stability: '',
    emergency_fund: '',
    buy_in_plan: '',
    periodic_investment_amount: '',
    initial_investment_amount: ''
  });

  // Update currency symbol when currency changes
  useEffect(() => {
    const handleCurrencyChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('currencyChange', handleCurrencyChange);
    return () => window.removeEventListener('currencyChange', handleCurrencyChange);
  }, []);

  const questions = {
    1: {
      title: 'Planning Goals',
      question: 'What is your primary planning goal?',
      field: 'planning_goal',
      options: [
        { value: 'ASSET', label: 'I want to get an asset' },
        { value: 'PROPERTY', label: 'I want to get a property' },
        { value: 'EDUCATION', label: "It's for education" },
        { value: 'EMERGENCY', label: 'Incase of emergency' }
      ]
    },
    2: {
      title: 'Investment Time Horizon',
      question: 'What is the duration of the plan?',
      field: 'investment_horizon',
      options: [
        { value: '4W', label: '4 weeks' },
        { value: '16W', label: '16 weeks' },
        { value: '32W', label: '32 weeks' },
        { value: '52W', label: '52 weeks' },
        { value: '1_5Y', label: '1-5 years' },
        { value: '5Y_PLUS', label: '5+ years' }
      ]
    },
    3: {
      title: 'Income Stability',
      question: 'How stable is your current income?',
      field: 'income_stability',
      options: [
        { value: 'UNSTABLE', label: 'Very unstable' },
        { value: 'STABLE', label: 'Fairly stable' },
        { value: 'VERY_STABLE', label: 'Very stable and predictable' }
      ]
    },
    4: {
      title: 'Emergency Fund',
      question: 'Do you have an emergency fund (3–6 months of expenses)?',
      field: 'emergency_fund',
      options: [
        { value: 'NO', label: 'No' },
        { value: 'PARTIAL', label: 'Partially' },
        { value: 'YES', label: 'Yes' }
      ]
    },
    5: {
      title: 'Contribution Plan',
      question: 'What is your contribution plan?',
      field: 'buy_in_plan',
      options: [
        { 
            value: 'PERIODIC_BUY', 
            label: 'I will add funds to this plan periodically (every 4 weeks)' 
        },
        { 
            value: 'ONE_TIME', 
            label: 'I will like to be auto reminded' 
        },
        { 
            value: 'NO_MORE_BUY', 
            label: 'I have no plans of adding more funds to this plan' 
        }
      ]
    },
    6: {
      title: 'Investment Amount',
      question: 'How much do you want to invest initially?',
      field: 'initial_investment_amount',
      type: 'amount'
    }
  };

  const currentQuestion = questions[currentStep];

  const handleOptionSelect = (value) => {
    // Validation for PERIODIC_BUY with 4W (5 minutes)
    if (currentStep === 5 && formData.investment_horizon === '4W') {
      if (value === 'PERIODIC_BUY') {
        setError('Periodic contributions are not available for 4-week plans. Please choose a longer investment horizon or select a different contribution option.');
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [currentQuestion.field]: value
    }));
    setError(null);
  };

  const handleAmountChange = (value) => {
    setFormData(prev => ({
      ...prev,
      [currentQuestion.field]: value
    }));
    setError(null);
  };

  const handleNext = async () => {
    if (!formData[currentQuestion.field]) {
      setError('Please provide an answer to continue');
      return;
    }

    // Validate amounts if on amount steps
    if (currentQuestion.type === 'amount') {
      const amount = parseFloat(formData[currentQuestion.field]);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid amount greater than 0');
        return;
      }

      // ✅ Minimum investment check — mirrors serializer's min_investment_ngn = 10,000
      const MIN_PLAN_NGN = 10000;
      try {
        let minDisplay = MIN_PLAN_NGN;
        let symbol = '₦';

        if (currency === 'USD') {
          const rateRes = await fetch(`${API_BASE_URL}/currency/rate/`, {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (rateRes.ok) {
            const rateData = await rateRes.json();
            minDisplay = MIN_PLAN_NGN * parseFloat(rateData.ngn_to_usd);
            symbol = '$';
          }
        }

        if (amount < minDisplay) {
          setError(`Minimum investment amount is ${symbol}${minDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
          return;
        }
      } catch (e) {
        // If rate fetch fails, let the backend validate
        console.warn('Could not fetch rate for minimum check, continuing to backend');
      }
    }
    
    if (currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    // Validate periodic investment amount if PERIODIC_BUY selected
    if (formData.buy_in_plan === 'PERIODIC_BUY') {
      if (!formData.periodic_investment_amount || parseFloat(formData.periodic_investment_amount) <= 0) {
        setError('Please enter a valid periodic investment amount');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Get the amounts in the current display currency
      let initialAmount = parseFloat(formData.initial_investment_amount);
      let periodicAmount = formData.periodic_investment_amount ? parseFloat(formData.periodic_investment_amount) : null;

      // If user is in USD, we need to convert to NGN before sending to backend
      // Backend stores everything in NGN
      if (currency === 'USD') {
        try {
          const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
          
          if (response.ok) {
            const rateData = await response.json();
            const usdToNgn = parseFloat(rateData.usd_to_ngn);
            
            // Convert USD amounts to NGN
            initialAmount = initialAmount * usdToNgn;
            if (periodicAmount) {
              periodicAmount = periodicAmount * usdToNgn;
            }
          } else {
            setError('Unable to get exchange rate. Please try again.');
            setLoading(false);
            return;
          }
        } catch (err) {
          setError('Failed to convert currency. Please try again.');
          setLoading(false);
          return;
        }
      }

      const questionnaireData = {
        planning_goal: formData.planning_goal,
        investment_horizon: formData.investment_horizon,
        income_stability: formData.income_stability,
        emergency_fund: formData.emergency_fund,
        buy_in_plan: formData.buy_in_plan,
        initial_investment_amount: initialAmount // Now in NGN
      };

      if (formData.buy_in_plan === 'PERIODIC_BUY') {
        questionnaireData.periodic_investment_amount = periodicAmount; // Now in NGN
      }

      const response = await planService.submitQuestionnaire(questionnaireData);
      onComplete(response.questionnaire);
    } catch (err) {
      console.error('❌ Questionnaire submission error:', err);

      // ✅ Extract the real error from the response body
      let message = null;
      try {
        if (err.response && err.response.data) {
          // axios-style
          const data = err.response.data;
          message = data.error || data.initial_investment_amount?.[0] || data.periodic_investment_amount?.[0] || data.buy_in_plan?.[0];
        } else if (err.message && err.message.includes('{')) {
          // fetch-style: error message contains raw JSON
          const jsonMatch = err.message.match(/\{.*\}/s);
          if (jsonMatch) {
            const data = JSON.parse(jsonMatch[0]);
            message = data.error || data.initial_investment_amount?.[0] || data.periodic_investment_amount?.[0] || data.buy_in_plan?.[0];
          }
        }
      } catch (parseErr) {
        console.warn('Could not parse error response:', parseErr);
      }

      setError(message || err.message || 'Failed to submit questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const isLastStep = currentStep === 6;
  const showPeriodicAmountInput = currentStep === 5 && formData.buy_in_plan === 'PERIODIC_BUY';
  const currencySymbol = getCurrencySymbol();

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header - Fixed */}
        <div className="plan-modal-header">
          <div>
            <h2>Investment Plan Questionnaire</h2>
            <p className="stock-name">Create your personalized investment plan</p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Progress Bar - Fixed */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / 6) * 100}%` }}
            />
          </div>
          <div className="plan-progress-text">Step {currentStep} of 6</div>
        </div>

        {/* Scrollable Content Wrapper */}
        <div className="question-content">
          <h3 className="question-title">{currentQuestion.title}</h3>
          <p className="question-text">{currentQuestion.question}</p>

          {currentQuestion.type === 'amount' ? (
            <div className="amount-input-container">
              <label>Investment Amount</label>
              <div className="amount-input">
                <span className="currency-symbol">{currencySymbol}</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData[currentQuestion.field]}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="Enter amount"
                  disabled={loading}
                />
              </div>
              <span className="helper-text">
                This amount will be deducted from your wallet to start the plan
              </span>
            </div>
          ) : (
            <div className="options-container">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option.value}
                  className={`option-button ${formData[currentQuestion.field] === option.value ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option.value)}
                  disabled={loading}
                >
                  <span className="option-letter">{String.fromCharCode(97 + index)})</span>
                  <span className="option-text">{option.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Periodic Investment Amount Input */}
          {showPeriodicAmountInput && (
            <div className="periodic-amount-input">
              <label>Periodic Contribution Amount ({currencySymbol})</label>
              <input
                type="number"
                step="0.01"
                value={formData.periodic_investment_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, periodic_investment_amount: e.target.value }))}
                placeholder="Enter amount to contribute every 4 weeks"
                disabled={loading}
              />
              <span className="helper-text">
                💡 This amount will be automatically added to your plan every 4 weeks
              </span>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>

        {/* Navigation Buttons - Fixed */}
        <div className="navigation-buttons">
          <button
            className="nav-button secondary"
            onClick={handlePrevious}
            disabled={currentStep === 1 || loading}
          >
            Previous
          </button>
          <button
            className="nav-button primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? 'Submitting...' : (isLastStep ? 'Submit' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanQuestionnaire;